import React, { useState } from 'react';
import { Button } from '../ui/button';
import axiosInstance from '../../api/api';
import { toast } from '../../hooks/use-toast';
import { X, ArrowsClockwise, FileText, CaretRight } from '@phosphor-icons/react';

const money = (n) => (n == null ? '—' : `${n < 0 ? '-' : ''}$${Math.abs(Number(n)).toLocaleString()}`);

export const ChangeOrderModal = ({ isOpen, onClose, context }) => {
  // context: { job_type, location, original_total, original_scope, source, source_id, prefill }
  const [description, setDescription] = useState('');
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (isOpen) {
      setDescription(context?.prefill || '');
      setResult(null);
    }
  }, [isOpen, context?.prefill]);

  if (!isOpen) return null;

  const generate = async () => {
    if (description.trim().length < 10) {
      toast({ title: 'Describe the change in a bit more detail', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const r = await axiosInstance.post('/ai-pricing/change-order', {
        change_description: description,
        job_type: context?.job_type || '',
        location: context?.location || '',
        original_total: context?.original_total ?? null,
        original_scope: context?.original_scope || null,
        source: context?.source || 'estimate',
        source_id: context?.source_id || null,
      });
      setResult(r.data);
    } catch (e) {
      toast({ title: 'Change order failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const copyText = () => {
    const o = result.order;
    const lines = [
      `CHANGE ORDER: ${o.title}`, '', o.summary, '',
      ...(o.line_items || []).map((li) => `• ${li.description} — ${money(li.total)}`),
      '', `Change total: ${money(o.total_change)}`,
      result.new_project_total != null ? `New project total: ${money(result.new_project_total)}` : '',
      o.timeline_impact_days ? `Timeline impact: +${o.timeline_impact_days} day(s)` : '',
    ].filter(Boolean);
    navigator.clipboard.writeText(lines.join('\n'));
    toast({ title: 'Change order copied — paste it into a message or email' });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center" data-testid="change-order-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full sm:max-w-xl bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 p-4 bg-[#2B4C6F] text-white flex items-center justify-between border-b-2 border-black z-10">
          <h3 className="font-black uppercase tracking-wide flex items-center gap-2">
            <ArrowsClockwise size={18} weight="bold" /> Change Order
          </h3>
          <button onClick={onClose} data-testid="close-change-order-modal"><X size={20} /></button>
        </div>

        <div className="p-4 space-y-4">
          {context?.original_total != null && (
            <p className="text-xs font-mono text-neutral-500">
              Original contract: <span className="font-bold">{money(context.original_total)}</span>
              {context?.job_type ? ` · ${context.job_type.replace(/_/g, ' ')}` : ''}
              {context?.location ? ` · ${context.location}` : ''}
            </p>
          )}

          <div>
            <label className="text-xs font-black uppercase tracking-wider text-neutral-500">What changed? *</label>
            <textarea
              className="w-full border-2 border-black dark:border-white p-3 bg-white dark:bg-neutral-800 dark:text-white mt-1 min-h-[90px]"
              placeholder="e.g. Client wants a second vanity added in the master bath, plus upgrade to quartz counters"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid="change-order-description"
            />
          </div>

          <Button
            onClick={generate}
            disabled={busy}
            className="w-full h-11 bg-[#D95800] hover:bg-[#B84A00] text-white font-black uppercase border-2 border-black rounded-none shadow-[3px_3px_0px_#000]"
            data-testid="generate-change-order-btn"
          >
            {busy ? 'Pricing the change…' : result ? 'Re-Generate' : 'Price This Change'}
          </Button>

          {result && (
            <div className="border-2 border-black dark:border-white" data-testid="change-order-result">
              <div className="p-3 bg-neutral-50 dark:bg-neutral-900 border-b-2 border-black dark:border-white">
                <h4 className="font-black uppercase text-sm text-neutral-900 dark:text-white">{result.order.title}</h4>
                <p className="text-xs text-neutral-600 dark:text-neutral-300 mt-1">{result.order.summary}</p>
              </div>
              <div className="divide-y divide-neutral-200 dark:divide-neutral-700">
                {(result.order.line_items || []).map((li, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                    <span className="text-neutral-700 dark:text-neutral-300 flex items-center gap-1 min-w-0">
                      <CaretRight size={12} className="shrink-0 text-[#D95800]" /> <span className="truncate">{li.description}</span>
                    </span>
                    <span className="font-mono font-bold text-neutral-900 dark:text-white shrink-0">{money(li.total)}</span>
                  </div>
                ))}
              </div>
              <div className="p-3 bg-[#2B4C6F] text-white space-y-1">
                <div className="flex justify-between font-black uppercase text-sm">
                  <span>Change Total</span>
                  <span className="font-mono" data-testid="change-order-total">{money(result.order.total_change)}</span>
                </div>
                {result.new_project_total != null && (
                  <div className="flex justify-between text-xs text-white/80">
                    <span>New project total</span>
                    <span className="font-mono font-bold" data-testid="new-project-total">{money(result.new_project_total)}</span>
                  </div>
                )}
                {result.order.timeline_impact_days > 0 && (
                  <p className="text-xs text-white/70">Timeline impact: +{result.order.timeline_impact_days} day(s)</p>
                )}
              </div>
              {result.order.client_note && (
                <p className="p-3 text-xs italic text-neutral-500 border-t border-neutral-200 dark:border-neutral-700">"{result.order.client_note}"</p>
              )}
              <div className="p-3 border-t-2 border-black dark:border-white">
                <Button
                  onClick={copyText}
                  variant="outline"
                  className="w-full h-9 text-xs font-bold uppercase border-2 border-black dark:border-white rounded-none"
                  data-testid="copy-change-order-btn"
                >
                  <FileText size={14} className="mr-1" /> Copy for Client
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
